import React, { useState } from "react";
import "./CartModal.css";
import { createPreference } from "../utils/productCache";
import { track } from "../utils/tracking";

const CartModal = ({ isOpen, onClose, cartItems, onRemove }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Resumen, 2: Formulario de envío

  // Tu identificador manual
  const userId = "32"; 

  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    direccion: ""
  });

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* =====================================================
     🔄 LÓGICA DE AGRUPACIÓN (Para visualización)
  ===================================================== */
  // Si el padre envía duplicados, esto los agrupa para mostrar "x Cantidad"
  const groupedItems = cartItems.reduce((acc, item) => {
    const existingItem = acc.find(i => i.product_name === item.product_name);
    if (existingItem) {
      existingItem.quantity = (existingItem.quantity || 1) + (item.quantity || 1);
    } else {
      acc.push({ ...item, quantity: item.quantity || 1 });
    }
    return acc;
  }, []);

  /* =====================================================
     💰 CÁLCULO DEL TOTAL
  ===================================================== */
  const total = groupedItems.reduce((acc, item) => {
    const rawPrice = item.cost || item.unit_price || 0;
    const cleanPrice = typeof rawPrice === "string"
        ? Number(rawPrice.replace(/\./g, "").replace(/[^0-9]/g, ""))
        : Number(rawPrice);
    
    return acc + (cleanPrice * item.quantity);
  }, 0);

  /* =====================================================
     🚀 CHECKOUT: ENVÍO CON PIPES (|)
  ===================================================== */
  const handleFinalCheckout = async (e) => {
    e.preventDefault();
    if (groupedItems.length === 0) return;

    setLoading(true);

    try {
      // Formateo sincronizado para BigQuery
      const nombresFormateados = groupedItems.map(i => i.product_name).join(" | ");
      const preciosFormateados = groupedItems.map(i => i.cost).join(" | ");
      const cantidadesFormateadas = groupedItems.map(i => i.quantity).join(" | ");

      const logData = {
        user_id_manual: userId,
        nombre_cliente: formData.nombre,
        telefono_cliente: formData.telefono,
        direccion_envio: formData.direccion,
        total_value: total,
        
        // Data en Pipes
        detalle_items: nombresFormateados,
        detalle_precios: preciosFormateados,
        detalle_cantidades: cantidadesFormateadas,
        
        cantidad_skus: groupedItems.length,
        timestamp_frontend: new Date().toISOString()
      };

      // Tracking a BigQuery
      track("purchase_attempt", logData);

      // Mercado Pago
      const externalRef = `FIORI-${crypto.randomUUID().slice(0, 8).toUpperCase()}`;
      const mpData = await createPreference(groupedItems, { 
        ...formData, 
        external_reference: externalRef 
      });

      if (mpData?.init_point) {
        window.location.assign(mpData.init_point);
      } else {
        throw new Error("Error al obtener link de pago");
      }

    } catch (error) {
      console.error("Checkout Error:", error);
      alert("Hubo un error al procesar el pedido.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cart-overlay">
      <div className="cart-container">
        
        {/* CABECERA */}
        <div className="cart-header">
          <h2>{step === 1 ? "Tu Carrito" : "Datos de Entrega"}</h2>
          <button className="cart-close-x" onClick={onClose} disabled={loading}>&times;</button>
        </div>

        {/* CONTENIDO DEL CARRITO */}
        <div className="cart-body">
          {step === 1 ? (
            groupedItems.length === 0 ? (
              <p className="cart-empty">El carrito está vacío.</p>
            ) : (
              <div className="cart-list">
                {groupedItems.map((item, index) => (
                  <div key={index} className="cart-item">
                    <div className="cart-item-img-container">
                      <img src={item.url} alt={item.product_name} />
                      {/* Badge de cantidad sobre la imagen o al lado */}
                      {item.quantity > 1 && (
                        <span className="cart-item-badge">x{item.quantity}</span>
                      )}
                    </div>
                    <div className="cart-item-info">
                      <h4>{item.product_name}</h4>
                      <p>{item.cost} {item.quantity > 1 ? `(u.)` : ""}</p>
                    </div>
                    <button 
                      className="cart-remove-btn" 
                      onClick={() => onRemove(index)}
                      disabled={loading}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* FORMULARIO */
            <form className="cart-form" id="checkout-form" onSubmit={handleFinalCheckout}>
              <div className="form-group">
                <label>Nombre Completo</label>
                <input type="text" name="nombre" value={formData.nombre} onChange={handleInputChange} required placeholder="Nombre..." />
              </div>
              <div className="form-group">
                <label>WhatsApp</label>
                <input type="tel" name="telefono" value={formData.telefono} onChange={handleInputChange} required placeholder="300..." />
              </div>
              <div className="form-group">
                <label>Dirección exacta</label>
                <textarea name="direccion" value={formData.direccion} onChange={handleInputChange} required placeholder="Calle, número, barrio..." />
              </div>
            </form>
          )}
        </div>

        {/* FOOTER */}
        <div className="cart-footer">
          <div className="cart-total">
            <span>Total:</span>
            <span>${total.toLocaleString()}</span>
          </div>

          <div className="cart-actions">
            {step === 1 ? (
              <>
                <button className="cart-btn-secondary" onClick={onClose}>Ver más</button>
                <button 
                  className="cart-btn-primary" 
                  onClick={() => setStep(2)}
                  disabled={groupedItems.length === 0}
                >
                  Confirmar Pedido
                </button>
              </>
            ) : (
              <>
                <button className="cart-btn-secondary" onClick={() => setStep(1)} disabled={loading}>Atrás</button>
                <button type="submit" form="checkout-form" className="cart-btn-primary" disabled={loading}>
                  {loading ? "Cargando..." : "Ir a Pagar"}
                </button>
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default CartModal;